"use client";

import React, { useState, useRef, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { LottieSvg } from "lottie-react";
import Link from "next/link";
import { homepageApi } from "../api/queries";
import { homepageService } from "../services";
import { PLACE_HOLDER_IMAGE } from "@/src/shared/constants/app";

/** What a tab needs to draw. */
interface IHomeTabItem {
  id: number;
  title: string;
  icon: string;
  active_icon?: string | null;
}

interface IHomeTabListProps<T extends IHomeTabItem> {
  tabs: T[];
  selectedTab: T | null;
  onSelectTab: (campaign: T) => void;
  /** Real link for crawlers / new tab; defaults to the tab's own page. */
  getHref?: (campaign: T) => string;
}

const HomeTabList = <T extends IHomeTabItem>({
  tabs,
  selectedTab,
  onSelectTab,
  getHref = homepageService.tabHref,
}: IHomeTabListProps<T>) => {
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(false);
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  // Parsed Lottie JSON per tab; null when the file failed to load.
  const [lottieDataMap, setLottieDataMap] = useState<
    Record<number, object | null>
  >({});
  const [isMobile, setIsMobile] = useState(false);
  const [imageStatus, setImageStatus] = useState<
    Record<number, "loading" | "loaded" | "error">
  >({});
  const [activeIconStatus, setActiveIconStatus] = useState<
    Record<number, "loading" | "loaded" | "error">
  >({});
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Preload and initialize image status for all tabs
  useEffect(() => {
    tabs.forEach((c) => {
      // Preload default icon
      const defaultImg = new Image();
      defaultImg.src = c.icon;

      if (defaultImg.complete) {
        setImageStatus((prev) => ({ ...prev, [c.id]: "loaded" }));
      } else {
        setImageStatus((prev) => ({ ...prev, [c.id]: "loading" }));
        defaultImg.onload = () =>
          setImageStatus((prev) => ({ ...prev, [c.id]: "loaded" }));
        defaultImg.onerror = () =>
          setImageStatus((prev) => ({ ...prev, [c.id]: "error" }));
      }

      // Preload active icon if it exists and is not a Lottie file
      if (
        c.active_icon &&
        typeof c.active_icon === "string" &&
        !c.active_icon.endsWith(".json")
      ) {
        const activeImg = new Image();
        activeImg.src = c.active_icon;

        if (activeImg.complete) {
          setActiveIconStatus((prev) => ({ ...prev, [c.id]: "loaded" }));
        } else {
          setActiveIconStatus((prev) => ({ ...prev, [c.id]: "loading" }));
          activeImg.onload = () =>
            setActiveIconStatus((prev) => ({ ...prev, [c.id]: "loaded" }));
          activeImg.onerror = () =>
            setActiveIconStatus((prev) => ({ ...prev, [c.id]: "error" }));
        }
      }
    });
  }, [tabs]);

  // Detect mobile view
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Scroll watcher (keep logic as-is)
  useEffect(() => {
    const handleScroll = () => {
      const currentScroll = window.scrollY;

      if (!isMobile) {
        setIsScrollingDown(false);
        return;
      }

      if (currentScroll > 10) {
        setIsScrollingDown(true);
      } else {
        setIsScrollingDown(false);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMobile]);

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      setShowLeftButton(scrollLeft > 10);
      setShowRightButton(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: direction === "left" ? -250 : 250,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      // Reset scroll position to 0 for proper centering (kept as-is)
      container.scrollLeft = 0;
      checkScrollButtons();
    }
  }, [tabs, isMobile]);

  const loadLottie = useCallback(
    async (campaignId: number, lottieUrl: string) => {
      try {
        const data = await homepageApi.getAnimation(lottieUrl);
        setLottieDataMap((prev) => ({ ...prev, [campaignId]: data }));
      } catch {
        setLottieDataMap((prev) => ({ ...prev, [campaignId]: null }));
      }
    },
    [],
  );

  /*
   * One request per animation, ever. A failure stores `null`, which is falsy —
   * so keying the guard off `lottieDataMap` meant a 404 re-armed itself, and
   * since writing the map re-ran this effect, a single missing file span the
   * fetch as fast as React could re-render it (~86k requests before Chrome
   * started answering ERR_INSUFFICIENT_RESOURCES). The ref records the attempt
   * instead of its outcome, and the map leaves the dependency list with it.
   */
  const requestedLottieRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    tabs.forEach((c) => {
      if (
        typeof c.active_icon !== "string" ||
        !c.active_icon.endsWith(".json")
      ) {
        return;
      }
      const key = `${c.id}:${c.active_icon}`;
      if (requestedLottieRef.current.has(key)) return;
      requestedLottieRef.current.add(key);
      void loadLottie(c.id, c.active_icon);
    });
  }, [tabs, loadLottie]);

  const handleImageLoad = (id: number) => {
    setImageStatus((prev) => ({ ...prev, [id]: "loaded" }));
  };
  const handleImageError = (id: number) => {
    setImageStatus((prev) => ({ ...prev, [id]: "error" }));
  };

  const handleActiveIconLoad = (id: number) => {
    setActiveIconStatus((prev) => ({ ...prev, [id]: "loaded" }));
  };
  const handleActiveIconError = (id: number) => {
    setActiveIconStatus((prev) => ({ ...prev, [id]: "error" }));
  };

  return (
    <div
      // Mobile: sticky under the search bar. It used to flip from relative to fixed, which
      // pulled it out of the flow and made the rails jump up by its height.
      className={`w-full transition-shadow duration-300 ${
        isMobile
          ? `sticky top-[70px] z-40 mt-[58px] bg-white px-1 shadow-md ${
              isScrollingDown ? "border-b border-gray-100" : ""
            }`
          : "relative px-1"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-center relative">
          <div className="relative w-full max-w-[824px] mx-auto">
            {showLeftButton && !isScrollingDown && (
              <button
                onClick={() => scroll("left")}
                className="absolute left-0 md:-left-2 top-1/2 -translate-y-1/2 z-20 p-1.5 sm:p-2 bg-white border border-gray-300 rounded-full hover:shadow-md transition-shadow shadow-sm"
              >
                <ChevronLeft size={16} />
              </button>
            )}

            <div
              ref={scrollContainerRef}
              className={`flex items-center gap-[20px] md:gap-[35px] overflow-x-auto scrollbar-hide px-2 md:px-8 w-full justify-start md:justify-center ${
                tabs.length < 4 ? "justify-center" : ""
              }`}
              onScroll={checkScrollButtons}
            >
              {tabs.map((campaign) => {
                const isSelected = selectedTab?.id === campaign.id;
                const isLottie =
                  typeof campaign.active_icon === "string" &&
                  campaign.active_icon.endsWith(".json");
                const lottieData = lottieDataMap[campaign.id];
                const showLottie = isLottie && !!lottieData;
                /*
                 * An animation that 404s resolves to `null` here, one still in
                 * flight to `undefined`. Either way the selected tab falls back
                 * to a still image rather than rendering an empty 32px slot.
                 */
                const staticActiveIcon = isLottie ? null : campaign.active_icon;
                const campaignUrl = getHref(campaign);
                return (
                  <Link
                    key={campaign.id}
                    href={campaignUrl}
                    prefetch={false}
                    onClick={(event) => {
                      // The href stays a real campaign URL so crawlers (and
                      // "open in new tab") still reach the prerendered page.
                      // A plain click switches tabs on the client instead, so
                      // the page — header included — is never re-rendered.
                      if (
                        event.metaKey ||
                        event.ctrlKey ||
                        event.shiftKey ||
                        event.altKey ||
                        event.button !== 0
                      ) {
                        return;
                      }
                      event.preventDefault();
                      onSelectTab(campaign);
                    }}
                    className={`flex flex-col items-center cursor-pointer transition-transform duration-300 hover:scale-105 flex-shrink-0 min-w-[100px] group ${
                      isSelected
                        ? "transform scale-105"
                        : "opacity-70 hover:opacity-100"
                    }`}
                  >
                    {/* MOBILE LAYOUT */}
                    <div className="md:hidden flex flex-col items-center relative w-full">
                      {/* Icon - hides on scroll down (SMOOTHER: fade + shrink) */}
                      <div
                        className={`transition-all duration-500 ease-in-out ${
                          isScrollingDown
                            ? "opacity-0 scale-90 -translate-y-1"
                            : "opacity-100 scale-100 translate-y-0"
                        } ${isScrollingDown ? "mb-0" : "mb-2"}`}
                      >
                        <div
                          className={`relative w-[32px] h-[32px] transition-transform duration-500 ease-in-out ${
                            isSelected ? "scale-110" : "scale-100"
                          }`}
                        >
                          {/* Default Icon */}
                          {!isSelected && (
                            <img
                              src={campaign.icon}
                              alt=""
                              width={32}
                              height={32}
                              style={{ objectFit: "cover" }}
                              className={`w-full h-full ${imageStatus[campaign.id] === "loading" ? "img-blur" : "img-sharp"}`}
                              onLoad={() => handleImageLoad(campaign.id)}
                              onError={(e) => {
                                handleImageError(campaign.id);
                                const target = e.target as HTMLImageElement;
                                target.onerror = null;
                                target.src = PLACE_HOLDER_IMAGE;
                              }}
                            />
                          )}

                          {/* Active icon: animation, still image, or default */}
                          {isSelected &&
                            (showLottie ? (
                              <LottieSvg
                                src={lottieData}
                                style={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "8px",
                                }}
                                loop={0}
                                autoplay={true}
                              />
                            ) : staticActiveIcon ? (
                              <img
                                src={staticActiveIcon}
                                alt=""
                                width={32}
                                height={32}
                                style={{ objectFit: "cover" }}
                                className={`w-full h-full ${activeIconStatus[campaign.id] === "loading" ? "img-blur" : "img-sharp"}`}
                                onLoad={() => handleActiveIconLoad(campaign.id)}
                                onError={(e) => {
                                  handleActiveIconError(campaign.id);
                                  const target = e.target as HTMLImageElement;
                                  target.onerror = null;
                                  target.src = campaign.icon;
                                }}
                              />
                            ) : (
                              <img
                                src={campaign.icon}
                                alt=""
                                width={32}
                                height={32}
                                style={{ objectFit: "cover" }}
                                className={`w-full h-full ${imageStatus[campaign.id] === "loading" ? "img-blur" : "img-sharp"}`}
                                onLoad={() => handleImageLoad(campaign.id)}
                                onError={(e) => {
                                  handleImageError(campaign.id);
                                  const target = e.target as HTMLImageElement;
                                  target.onerror = null;
                                  target.src = PLACE_HOLDER_IMAGE;
                                }}
                              />
                            ))}
                        </div>
                      </div>

                      {/* Text - always visible */}
                      <span
                        className={`text-xs font-semibold leading-[100%] whitespace-nowrap transition-colors duration-300 ${
                          isSelected ? "text-ink-black" : "text-ink-gray"
                        } ${isScrollingDown ? "!mb-2" : "mb-2"}`}
                      >
                        {campaign.title}
                      </span>

                      {/* Bottom border - full width */}
                      <div
                        className={`absolute bottom-0 left-0 right-0 h-[3px]  border-b-4 border-neutral-900 rounded-t-[4px] transition-all duration-300 ${
                          isSelected
                            ? "opacity-100 scale-x-100"
                            : "opacity-0 scale-x-0"
                        }`}
                      />
                    </div>

                    {/* DESKTOP LAYOUT - Icon on left, text on right */}
                    <div className="hidden md:flex md:flex-col md:items-center w-full relative">
                      <div className="flex items-center gap-2 pb-1 ">
                        <div
                          className={`relative w-[32px] h-[32px]  overflow-hidden
                                                      transition-all duration-500 ease-in-out transform
                                                      ${isSelected ? "scale-110" : "scale-100"}
                                                      ${isScrollingDown ? "opacity-0 scale-90 -translate-y-1" : "opacity-100 scale-100 translate-y-0"}`}
                        >
                          {/* Default Icon */}
                          {!isSelected && (
                            <img
                              src={campaign.icon}
                              alt=""
                              width={32}
                              height={32}
                              style={{ objectFit: "cover" }}
                              className={`w-full h-full ${imageStatus[campaign.id] === "loading" ? "img-blur" : "img-sharp"}`}
                              onLoad={() => handleImageLoad(campaign.id)}
                              onError={(e) => {
                                handleImageError(campaign.id);
                                const target = e.target as HTMLImageElement;
                                target.onerror = null;
                                target.src = PLACE_HOLDER_IMAGE;
                              }}
                            />
                          )}

                          {/* Active icon: animation, still image, or default */}
                          {isSelected &&
                            (showLottie ? (
                              <LottieSvg
                                src={lottieData}
                                style={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "8px",
                                }}
                                loop={0}
                                autoplay={true}
                              />
                            ) : staticActiveIcon ? (
                              <img
                                src={staticActiveIcon}
                                alt=""
                                width={32}
                                height={32}
                                style={{ objectFit: "cover" }}
                                className={`w-full h-full ${activeIconStatus[campaign.id] === "loading" ? "img-blur" : "img-sharp"}`}
                                onLoad={() => handleActiveIconLoad(campaign.id)}
                                onError={(e) => {
                                  handleActiveIconError(campaign.id);
                                  const target = e.target as HTMLImageElement;
                                  target.onerror = null;
                                  target.src = campaign.icon;
                                }}
                              />
                            ) : (
                              <img
                                src={campaign.icon}
                                alt=""
                                width={32}
                                height={32}
                                style={{ objectFit: "cover" }}
                                className={`w-full h-full ${imageStatus[campaign.id] === "loading" ? "img-blur" : "img-sharp"}`}
                                onLoad={() => handleImageLoad(campaign.id)}
                                onError={(e) => {
                                  handleImageError(campaign.id);
                                  const target = e.target as HTMLImageElement;
                                  target.onerror = null;
                                  target.src = PLACE_HOLDER_IMAGE;
                                }}
                              />
                            ))}
                        </div>

                        <span
                          className={`text-xs font-semibold leading-[100%] whitespace-nowrap transition-colors duration-300 ${
                            isSelected ? "text-ink-black" : "text-ink-gray"
                          }`}
                        >
                          {campaign.title}
                        </span>
                      </div>

                      {/* Bottom border spanning both icon and text */}
                      <div
                        className={`w-[80%] h-[3px]  border-b-4 border-neutral-900 rounded-t-[4px] transition-all duration-300 ${
                          isSelected
                            ? "opacity-100 scale-x-100"
                            : "opacity-0 scale-x-0"
                        }`}
                      />
                    </div>
                  </Link>
                );
              })}
            </div>

            {showRightButton && !isScrollingDown && (
              <button
                onClick={() => scroll("right")}
                className="absolute right-0 md:-right-2 top-1/2 -translate-y-1/2 z-20 p-1.5 sm:p-2 bg-white border border-gray-300 rounded-full hover:shadow-md transition-shadow shadow-sm"
              >
                <ChevronRight size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeTabList;
